import { useState, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import styles from './FilterGroup.module.scss';

export interface FilterOption {
  id: string;
  name: string;
  disabled?: boolean;
}

interface FilterGroupProps {
  title: string;
  options: FilterOption[];
  selectedValues: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const FilterGroup = ({
  title,
  options,
  selectedValues,
  onSelectionChange,
  isLoading = false,
  disabled = false,
  expanded = false,
  onExpandedChange
}: FilterGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [searchQuery, setSearchQuery] = useState('');

  const handleExpandedChange = (_event: React.SyntheticEvent, newExpanded: boolean) => {
    setIsExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  const handleCheckboxChange = (optionId: string) => {
    const newSelectedValues = selectedValues.includes(optionId)
      ? selectedValues.filter(id => id !== optionId)
      : [...selectedValues, optionId];
    onSelectionChange(newSelectedValues);
  };

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const normalizedQuery = searchQuery.toLowerCase();
    return options.filter(option => 
      option.name.toLowerCase().includes(normalizedQuery)
    );
  }, [options, searchQuery]);

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleExpandedChange}
      className={styles.accordion}
      disabled={disabled}
      sx={{
        '& .MuiButtonBase-root': {
          '&:focus': {
            outline: 'none !important',
            bgcolor: 'transparent !important',
          },
          '&.Mui-focusVisible': {
            outline: 'none !important',
            bgcolor: 'transparent !important',
          },
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={styles.summary}
        sx={{
          '&.Mui-focusVisible': {
            bgcolor: 'transparent !important',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            '& .MuiIconButton-root': {
              outline: 'none !important',
              '&:focus': {
                outline: 'none !important',
                bgcolor: 'transparent !important',
              },
              '&:hover': {
                bgcolor: 'transparent !important',
              },
            },
          },
        }}
      >
        <div className={styles.titleContainer}>
          <Typography className={styles.title}>{title}</Typography>
          {selectedValues.length > 0 && (
            <Typography className={styles.count}>
              ({selectedValues.length})
            </Typography>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails className={styles.details}>
        {isLoading ? (
          <div className={styles.loader}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormGroup 
              className={styles.optionsContainer}
              sx={{ 
                flexWrap: 'nowrap !important',
                flexDirection: 'column !important' 
              }}
            >
              {filteredOptions.map((option) => (
                <FormControlLabel
                  key={option.id}
                  control={
                    <Checkbox 
                      sx={{margin:0, padding:0, paddingRight:1, justifyContent: 'flex-start'}}
                      checked={selectedValues.includes(option.id)}
                      onChange={() => handleCheckboxChange(option.id)}
                      disabled={option.disabled}
                    />
                  }
                  label={option.name}
                  className={styles.option}
                />
              ))}
              {filteredOptions.length === 0 && (
                <Typography className={styles.noResults}>
                  No results found
                </Typography>
              )}
            </FormGroup>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default FilterGroup; 